import React from 'react';

import { ErrorMessage } from '@eika/attention-message';
import { ButtonTypes, SmallButton } from '@eika/button';
import { IconTypes } from '@eika/icon';

import { useConfig } from '../../../defaultConfig';
import usePushNotificationRules from '../../../fetchHooks/usePushNotificationRules';
import useTranslation from '../../../hooks/useTranslation';
import AccountTransactionNotificationSettings from './AccountTransactionNotificationSettings';
import BooleanNotificationToggle from './BooleanNotificationToggle';
import NotificationCategory from './NotificationCategory';
import SalaryNotificationSettings from './SalaryNotificationSettings';
import AccountTransactionNotificationSettingsSkeleton from './skeleton/AccountTransactionNotificationSettingsSkeleton';
import NotificationSettingsCategorySkeleton from './skeleton/NotificationSettingsCategorySkeleton';
import SalaryNotificationSettingsSkeleton from './skeleton/SalaryNotificationSettingsSkeleton';

const NotificationRules: React.FC = () => {
    const { featureToggles } = useConfig();
    const { pushNotifications: pushNotificationTexts } = useTranslation();

    const notificationRules = usePushNotificationRules();

    if (notificationRules.isError) {
        return (
            <div className="notifications-settings__error-message">
                <ErrorMessage
                    icon={IconTypes.UTROPSTEGN_24}
                    text={pushNotificationTexts.errorFetchRules}
                    clickableItem={
                        <SmallButton
                            buttonType={ButtonTypes.SMALL_NEGATIVE}
                            onClick={() => notificationRules.refetch()}
                        >
                            {pushNotificationTexts.errorRetry}
                        </SmallButton>
                    }
                />
            </div>
        );
    }

    return (
        <>
            <h2 className="notifications-settings__rule-heading">{pushNotificationTexts.chooseNotificationsToGet}</h2>
            <NotificationCategory
                stateLabel={pushNotificationTexts.salary.title}
                enabled={featureToggles.rules.salary}
                customSkeleton={<SalaryNotificationSettingsSkeleton />}
            >
                <SalaryNotificationSettings />
            </NotificationCategory>
            <NotificationCategory header={pushNotificationTexts.category.efaktura}>
                <BooleanNotificationToggle
                    notificationType="EFAKTURA_EVENT"
                    notificationRules={notificationRules.data}
                    isLoading={notificationRules.isLoading}
                />
                <BooleanNotificationToggle
                    notificationType="EFAKTURA_WITH_AVTALEGIRO_EVENT"
                    notificationRules={notificationRules.data}
                    isLoading={notificationRules.isLoading}
                />
                <BooleanNotificationToggle
                    notificationType="SHARED_EFAKTURA_EVENT"
                    notificationRules={notificationRules.data}
                    isLoading={notificationRules.isLoading}
                />

                {featureToggles.rules.overdueEfaktura && (
                    <BooleanNotificationToggle
                        notificationType="OVERDUE_EFAKTURA_EVENT"
                        notificationRules={notificationRules.data}
                        isLoading={notificationRules.isLoading}
                    />
                )}
            </NotificationCategory>

            <NotificationCategory
                header={pushNotificationTexts.category.account}
                customSkeleton={
                    <NotificationSettingsCategorySkeleton header={pushNotificationTexts.category.account} count={1}>
                        <AccountTransactionNotificationSettingsSkeleton />
                    </NotificationSettingsCategorySkeleton>
                }
            >
                <BooleanNotificationToggle
                    notificationType="SHARED_ACCOUNT"
                    notificationRules={notificationRules.data}
                    isLoading={notificationRules.isLoading}
                />
                {featureToggles.rules.account && <AccountTransactionNotificationSettings />}
            </NotificationCategory>
            <NotificationCategory header={pushNotificationTexts.category.loan} enabled={featureToggles.rules.loans}>
                <BooleanNotificationToggle
                    notificationType="LOAN_APPLICATION_EVENT"
                    notificationRules={notificationRules.data}
                    isLoading={notificationRules.isLoading}
                />
            </NotificationCategory>
            <BooleanNotificationToggle
                enabled={featureToggles.rules.stoppedPayment}
                notificationType="STOPPED_PAYMENT_EVENT"
                labelOverride={pushNotificationTexts.category.payments}
                notificationRules={notificationRules.data}
                isLoading={notificationRules.isLoading}
            />
            <BooleanNotificationToggle
                notificationType="MESSAGE_EVENT"
                labelOverride={pushNotificationTexts.category.messages}
                notificationRules={notificationRules.data}
                isLoading={notificationRules.isLoading}
            />
        </>
    );
};

export default NotificationRules;
